import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0;
import { action, computed, observable } from 'mobx';
import { isInSubnet } from 'core/util/ip';
import { fetchBgpSubPrefixes, fetchBgpReachabilityTimeseries, fetchBgpASPathGraphTimeseries } from './bgpRequests';

/** Minimal root store shape for cross-store access */
let BgpStore = (_class = class BgpStore {
  constructor() {
    /** Set by root store constructor — provides cross-store access */
    this.store = void 0;
    _initializerDefineProperty(this, "subPrefixesLoading", _descriptor, this);
    _initializerDefineProperty(this, "subPrefixesData", _descriptor2, this);
    _initializerDefineProperty(this, "subPrefixesError", _descriptor3, this);
    _initializerDefineProperty(this, "reachabilityTimeseriesLoading", _descriptor4, this);
    _initializerDefineProperty(this, "reachabilityTimeseriesData", _descriptor5, this);
    _initializerDefineProperty(this, "reachabilityTimeseriesError", _descriptor6, this);
    _initializerDefineProperty(this, "asPathGraphTimeseriesLoading", _descriptor7, this);
    _initializerDefineProperty(this, "asPathGraphTimeseriesData", _descriptor8, this);
    _initializerDefineProperty(this, "asPathGraphTimeseriesError", _descriptor9, this);
    _initializerDefineProperty(this, "asnColorMap", _descriptor0, this);
    // Frozen AS-path column-vertical adjacency (Tier 3, backup). Kept from the layout with the most
    // vertical edges the PathGraph has reported for the current data load, so colors stay stable
    // while scrubbing (each snapshot re-lays-out).
    this.pathGraphVerticalEdges = [];
  }
  reset() {
    this.subPrefixesLoading = false;
    this.subPrefixesData = null;
    this.subPrefixesError = null;
    this.reachabilityTimeseriesLoading = false;
    this.reachabilityTimeseriesData = null;
    this.reachabilityTimeseriesError = null;
    this.asPathGraphTimeseriesLoading = false;
    this.asPathGraphTimeseriesData = null;
    this.asPathGraphTimeseriesError = null;
    this.asnColorMap.clear();
    this.pathGraphVerticalEdges = [];
  }
  setSubPrefixesLoading(loading) {
    this.subPrefixesLoading = loading;
  }
  setSubPrefixesData(data) {
    this.subPrefixesData = data.map(pfx => pfx.trim());
    this.subPrefixesError = null;
  }
  setSubPrefixesError(error) {
    this.subPrefixesError = error;
    this.subPrefixesData = null;
  }
  setReachabilityTimeseriesLoading(loading) {
    this.reachabilityTimeseriesLoading = loading;
  }
  setReachabilityTimeseriesData(data) {
    this.reachabilityTimeseriesData = data;
    this.reachabilityTimeseriesError = null;

    // Recompute the shared ASN color map now that band adjacency may have changed
    this.recomputeAsnColors();
  }
  setReachabilityTimeseriesError(error) {
    this.reachabilityTimeseriesError = error;
    this.reachabilityTimeseriesData = null;
  }
  setASPathGraphTimeseriesLoading(loading) {
    this.asPathGraphTimeseriesLoading = loading;
  }
  setASPathGraphTimeseriesData(data) {
    this.asPathGraphTimeseriesData = data;
    this.asPathGraphTimeseriesError = null;

    // Recompute the shared ASN color map now that AS-path adjacency is available
    this.recomputeAsnColors();
  }
  setASPathGraphTimeseriesError(error) {
    this.asPathGraphTimeseriesError = error;
    this.asPathGraphTimeseriesData = null;
  }
  get hasSubPrefixes() {
    return Boolean(this.subPrefixesData && this.subPrefixesData.length > 0);
  }
  get subPrefixesByPrefix() {
    return prefix => {
      if (!this.subPrefixesData || this.subPrefixesData.length === 0) {
        return [];
      }
      return this.subPrefixesData.filter(subPfx => isInSubnet(subPfx, prefix));
    };
  }
  get isLoading() {
    return this.subPrefixesLoading || this.reachabilityTimeseriesLoading || this.asPathGraphTimeseriesLoading;
  }

  /**
   * Store column-vertical adjacency reported by the AS Path Visualization (Tier 3, backup).
   *
   * Only the layout with the **most** vertical edges seen for the current data load is kept, so
   * scrubbing the timeline (which re-lays-out per snapshot) never changes colors and edgeless
   * snapshots are ignored. Triggers a deterministic recompute when a denser layout arrives.
   */
  setPathGraphVerticalAdjacency(edges) {
    if (edges.length <= this.pathGraphVerticalEdges.length) {
      return;
    }
    this.pathGraphVerticalEdges = edges;
    this.recomputeAsnColors();
  }

  /** Add an undirected edge to an adjacency map (skips empty ids and self-loops). */
  static addAdjacencyEdge(adjacency, a, b) {
    if (!a || !b || a === b) {
      return;
    }
    const na = adjacency.get(a) ?? new Set();
    na.add(b);
    adjacency.set(a, na);
    const nb = adjacency.get(b) ?? new Set();
    nb.add(a);
    adjacency.set(b, nb);
  }

  /**
   * Tier 1 (hard): reachability band adjacency. Two ASNs are adjacent when their series are
   * consecutive in timeseries[prefix][groupBy] — i.e. their stacked bands touch. Built for all
   * three ASN groupings so any groupBy toggle is safe.
   */
  buildReachabilityAdjacency() {
    const adjacency = new Map();
    const timeseries = this.reachabilityTimeseriesData?.timeseries;
    if (timeseries) {
      const groupings = ['origin_asn', 'upstream_asn', 'second_upstream_asn'];
      for (const prefixData of Object.values(timeseries)) {
        for (const grouping of groupings) {
          const series = prefixData?.[grouping] ?? [];
          for (let i = 0; i < series.length - 1; i += 1) {
            BgpStore.addAdjacencyEdge(adjacency, series[i].dimension, series[i + 1].dimension);
          }
        }
      }
    }
    return adjacency;
  }

  /**
   * Tier 2 (soft): AS-path direct links. Two ASNs are adjacent when they appear consecutively in
   * any observed as_path (union over all routes → stable, timestamp-independent).
   */
  buildAsPathAdjacency() {
    const adjacency = new Map();
    const routes = this.asPathGraphTimeseriesData?.routes;
    if (routes) {
      for (const route of Object.values(routes)) {
        const path = route?.as_path ?? [];
        for (let i = 0; i < path.length - 1; i += 1) {
          BgpStore.addAdjacencyEdge(adjacency, String(path[i]), String(path[i + 1]));
        }
      }
    }
    return adjacency;
  }

  /** Tier 3 (backup): frozen column-vertical adjacency reported by the PathGraph. */
  buildVerticalAdjacency() {
    const adjacency = new Map();
    for (const [a, b] of this.pathGraphVerticalEdges) {
      BgpStore.addAdjacencyEdge(adjacency, a, b);
    }
    return adjacency;
  }

  /**
   * Determine the order ASNs are colored in. Walk the reachability stacks first (so the critical
   * stacked view alternates cleanly), then any remaining ASNs in numeric order.
   */
  buildColoringOrder(asnSet) {
    const order = [];
    const seen = new Set();
    const push = id => {
      if (asnSet.has(id) && !seen.has(id)) {
        seen.add(id);
        order.push(id);
      }
    };
    const timeseries = this.reachabilityTimeseriesData?.timeseries;
    if (timeseries) {
      const groupings = ['origin_asn', 'upstream_asn', 'second_upstream_asn'];
      for (const prefixData of Object.values(timeseries)) {
        for (const grouping of groupings) {
          for (const entry of prefixData?.[grouping] ?? []) {
            push(entry.dimension);
          }
        }
      }
    }
    [...asnSet].filter(id => !seen.has(id)).sort((a, b) => Number(a) - Number(b)).forEach(id => order.push(id));
    return order;
  }

  /** Lexicographic less-than for equal-length numeric tuples. */
  static lexLess(a, b) {
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        return a[i] < b[i];
      }
    }
    return false;
  }

  /**
   * Recompute the shared ASN -> color map using existing palette colors only.
   *
   * Deterministic, adjacency-aware greedy coloring with three strictly prioritised tiers:
   *   Tier 1 reachability band adjacency (hard — never traded away),
   *   Tier 2 AS-path direct links (soft),
   *   Tier 3 column-vertical adjacency (backup).
   * Each ASN takes the palette color with the lowest lexicographic cost
   * [tier1Conflicts, tier2Conflicts, tier3Conflicts, usageCount, index]. Because tier-1 conflicts
   * dominate, a reachability pair only ever shares a color when every color collides (genuinely
   * unavoidable), so lower tiers can never override reachability. usageCount spreads unavoidable
   * reuse across the palette; index keeps it deterministic. Result is shared by both views.
   */
  recomputeAsnColors() {
    const {
      qualitativeColors
    } = this.store.$colors;
    if (!qualitativeColors || qualitativeColors.length === 0) {
      return;
    }
    const palette = qualitativeColors;
    const asnSet = new Set();
    const reachAsns = this.reachabilityTimeseriesData?.lookups?.asns;
    if (reachAsns) {
      Object.keys(reachAsns).forEach(id => asnSet.add(id));
    }
    const aspathAsns = this.asPathGraphTimeseriesData?.lookups?.asns;
    if (aspathAsns) {
      Object.keys(aspathAsns).forEach(id => asnSet.add(id));
    }
    if (asnSet.size === 0) {
      return;
    }
    const tier1 = this.buildReachabilityAdjacency();
    const tier2 = this.buildAsPathAdjacency();
    const tier3 = this.buildVerticalAdjacency();
    const order = this.buildColoringOrder(asnSet);
    const assigned = new Map();
    const usage = new Array(palette.length).fill(0);
    const conflicts = (neighbors, color) => {
      if (!neighbors) {
        return 0;
      }
      let count = 0;
      neighbors.forEach(n => {
        if (assigned.get(n) === color) {
          count += 1;
        }
      });
      return count;
    };
    for (const id of order) {
      const n1 = tier1.get(id);
      const n2 = tier2.get(id);
      const n3 = tier3.get(id);
      let bestIndex = 0;
      let bestCost = null;
      for (let ci = 0; ci < palette.length; ci += 1) {
        const color = palette[ci];
        const cost = [conflicts(n1, color), conflicts(n2, color), conflicts(n3, color), usage[ci], ci];
        if (bestCost === null || BgpStore.lexLess(cost, bestCost)) {
          bestCost = cost;
          bestIndex = ci;
        }
      }
      assigned.set(id, palette[bestIndex]);
      usage[bestIndex] += 1;
    }
    this.asnColorMap.replace(assigned);
  }

  /**
   * Fetch sub-prefixes for the given monitored prefixes.
   * Sets subPrefixesData/subPrefixesError and manages loading state.
   */
  async fetchSubPrefixes({
    prefixes,
    includeCovered,
    startDate,
    endDate
  }) {
    this.setSubPrefixesLoading(true);
    try {
      const data = await fetchBgpSubPrefixes({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate
      });
      this.setSubPrefixesData(data);
      return data;
    } catch (error) {
      this.setSubPrefixesError(error);
      throw error;
    } finally {
      this.setSubPrefixesLoading(false);
    }
  }

  /**
   * Fetch reachability timeseries for the given prefixes.
   * Sets reachabilityTimeseriesData/Error, registers new ASNs in the color map.
   */
  async fetchReachabilityTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys
  }) {
    this.setReachabilityTimeseriesLoading(true);
    try {
      const data = await fetchBgpReachabilityTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys
      });
      this.setReachabilityTimeseriesData(data);
      return data;
    } catch (error) {
      this.setReachabilityTimeseriesError(error);
      throw error;
    } finally {
      this.setReachabilityTimeseriesLoading(false);
    }
  }

  /**
   * Fetch AS Path Graph timeseries for the given prefixes.
   * Sets asPathGraphTimeseriesData/Error, registers new ASNs in the color map.
   */
  async fetchASPathGraphTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys,
    pruningPercentage
  }) {
    this.setASPathGraphTimeseriesLoading(true);
    try {
      const data = await fetchBgpASPathGraphTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys,
        pruning_percentage: pruningPercentage
      });
      this.setASPathGraphTimeseriesData(data);
      return data;
    } catch (error) {
      this.setASPathGraphTimeseriesError(error);
      throw error;
    } finally {
      this.setASPathGraphTimeseriesLoading(false);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "subPrefixesLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "subPrefixesData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "subPrefixesError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "reachabilityTimeseriesLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "reachabilityTimeseriesData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "reachabilityTimeseriesError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "asPathGraphTimeseriesLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "asPathGraphTimeseriesData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "asPathGraphTimeseriesError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "asnColorMap", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return observable.map();
  }
}), _applyDecoratedDescriptor(_class.prototype, "reset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSubPrefixesLoading", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSubPrefixesLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSubPrefixesData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSubPrefixesData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSubPrefixesError", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSubPrefixesError"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setReachabilityTimeseriesLoading", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setReachabilityTimeseriesLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setReachabilityTimeseriesData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setReachabilityTimeseriesData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setReachabilityTimeseriesError", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setReachabilityTimeseriesError"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setASPathGraphTimeseriesLoading", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setASPathGraphTimeseriesLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setASPathGraphTimeseriesData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setASPathGraphTimeseriesData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setASPathGraphTimeseriesError", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setASPathGraphTimeseriesError"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasSubPrefixes", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasSubPrefixes"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "subPrefixesByPrefix", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "subPrefixesByPrefix"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isLoading", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setPathGraphVerticalAdjacency", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setPathGraphVerticalAdjacency"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "recomputeAsnColors", [action], Object.getOwnPropertyDescriptor(_class.prototype, "recomputeAsnColors"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetchSubPrefixes", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchSubPrefixes"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetchReachabilityTimeseries", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchReachabilityTimeseries"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetchASPathGraphTimeseries", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchASPathGraphTimeseries"), _class.prototype), _class);
export default new BgpStore();